import type {
  GridColDef,
  GridGroupingColDefOverride,
  GridInitialState,
  GridRenderCellParams,
  GridRowClassNameParams,
  GridRowHeightParams,
  GridRowParams,
  GridRowSelectionModel,
  GridSortDirection,
} from "@mui/x-data-grid-premium";
import { GridCellCheckboxRenderer, isAutogeneratedRow, useGridApiRef } from "@mui/x-data-grid-premium";
import { useCallback, useMemo, useState } from "react";
import type { AccessPermission } from "@entrilia/shared/api/types";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import {
  DataGridColumnHeadersWithToolbar,
  DataGridHeaderToolbarProvider,
} from "@entrilia/shared/components/grid/DataGridColumnHeadersWithToolbar";
import { getRowSelectionModelFromIds, getSelectedRowIdsFromModel } from "@entrilia/shared/components/grid/gridHelper";
import { convertISODateTime } from "@entrilia/shared/utilities/dateUtils";
import GroupHeaderWithSelection from "../../../common/grid/GroupHeaderWithSelection";
import { CustomToolbarActions } from "../report-lists/reports/grid/CustomToolbarActions";
import { getGroupedNameSortComparator } from "../report-lists/reports/grid/groupedNameSortComparator";
import GroupName from "../report-lists/reports/grid/GroupName";
import GroupSharingCell from "../report-lists/reports/grid/GroupSharingCell";
import { getReportsGridSx } from "../report-lists/reports/grid/reportsGridSx";
import SharingCell from "../report-lists/reports/grid/SharingCell";
import useGridExpand from "../report-lists/reports/hooks/useGridExpand";
import type { ArtifactActionHandlers } from "./list/ArtifactActions";
import ArtifactActions from "./list/ArtifactActions";
import { toBulkActionReport } from "./list/artifactBulkActions";
import ArtifactNameCell from "./list/ArtifactNameCell";
import type { ArtifactRow } from "./list/artifactRow";
import { getArtifactStatus } from "./list/artifactStatus";
import ArtifactStatusChip from "./list/ArtifactStatusChip";
import { getArtifactViewUrl } from "./list/artifactUrls";

interface Props extends ArtifactActionHandlers {
  rows: ArtifactRow[];
  loading: boolean;
  noRowsText: string;
  canManage: boolean;
  isGroupSelected: boolean;
  onToggleFavorite: (artifactId: string) => void;
}

const ARTIFACT_ROW_HEIGHT = 56;

const selectionPermissions: AccessPermission[] = ["ManageReports"];

const gridSlots = { columnHeaders: DataGridColumnHeadersWithToolbar };

const toolbarStyle = { paddingLeft: 0 };

const sortingOrder: GridSortDirection[] = ["asc", "desc"];

const initialGridState: GridInitialState = {
  sorting: { sortModel: [{ field: "modifiedAt", sort: "desc" }] },
};

const getRowId = (row: ArtifactRow) => row.id;

const getRowHeight = ({ model }: GridRowHeightParams) => (isAutogeneratedRow(model) ? undefined : ARTIFACT_ROW_HEIGHT);

const isRowSelectable = ({ row }: GridRowParams<ArtifactRow>) => !isAutogeneratedRow(row);

const getGroupedTreeDataPath = (row: ArtifactRow) => (row.group ? [row.group, row.id] : [row.id]);

const getFlatTreeDataPath = (row: ArtifactRow) => [row.id];

const compareStatusLabels = (left: string | undefined, right: string | undefined) =>
  (left ?? "").localeCompare(right ?? "", undefined, { numeric: true });

interface MenuHandlers extends ArtifactActionHandlers {
  onMenuToggle: (artifactId: string | undefined) => void;
}

const getColumns = (handlers: MenuHandlers): GridColDef<ArtifactRow>[] => [
  {
    field: "authorization",
    headerName: "Sharing",
    flex: 0.6,
    valueGetter: (_, row) => (isAutogeneratedRow(row) ? undefined : row.authorization.private),
    renderCell: ({ row, rowNode }) =>
      rowNode.type === "group" ? (
        <GroupSharingCell caption={String(rowNode.groupingKey ?? "")} />
      ) : (
        <SharingCell report={row} itemType="dashboard" onShareClick={handlers.onShare} />
      ),
  },
  {
    field: "status",
    headerName: "Status",
    flex: 1,
    valueGetter: (_, row) => (isAutogeneratedRow(row) ? undefined : getArtifactStatus(row).label),
    sortComparator: compareStatusLabels,
    renderCell: ({ row, rowNode }) => (rowNode.type === "group" ? null : <ArtifactStatusChip artifact={row} />),
  },
  {
    field: "createdBy",
    headerName: "Created by",
    flex: 1,
  },
  {
    field: "modifiedBy",
    headerName: "Modified by",
    flex: 1,
    valueGetter: (_, row) => row.modifiedBy ?? row.createdBy,
  },
  {
    field: "modifiedAt",
    headerName: "Modified at",
    flex: 1,
    valueGetter: (_, row) => row.modifiedAt ?? row.createdAt,
    valueFormatter: (value) => convertISODateTime(value),
  },
  {
    field: "actions",
    headerName: "Actions",
    width: 80,
    sortable: false,
    resizable: false,
    cellClassName: "grid-row-actions",
    align: "right",
    headerAlign: "right",
    renderCell: ({ row, rowNode }) =>
      rowNode.type === "group" ? null : <ArtifactActions artifact={row} {...handlers} />,
  },
];

const ArtifactsGrid = ({
  rows,
  loading,
  noRowsText,
  canManage,
  isGroupSelected,
  onToggleFavorite,
  onChangeGroup,
  onShare,
  onDelete,
}: Props) => {
  const apiRef = useGridApiRef();
  const { isGroupExpandedByDefault, toggleSingleGridRow, toggleAllGridRows } = useGridExpand({
    gridKey: "AI_DASHBOARDS_GRID",
    apiRef,
  });
  const [menuArtifactId, setMenuArtifactId] = useState<string>();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const groupingColDef = useMemo(
    (): GridGroupingColDefOverride<ArtifactRow> => ({
      headerName: "Name",
      hideDescendantCount: true,
      flex: 2,
      sortable: true,
      getSortComparator: getGroupedNameSortComparator(apiRef),
      renderHeader: (params) => (
        <GroupHeaderWithSelection
          headerName={params.colDef.headerName}
          permissions={selectionPermissions}
          hideExpandCollapse={isGroupSelected}
          onExpandToggle={toggleAllGridRows}
        />
      ),
      renderCell: (params: GridRenderCellParams<ArtifactRow>) =>
        params.rowNode.type === "group" ? (
          <GroupName params={params} permissions={selectionPermissions} onExpandToggle={toggleSingleGridRow} />
        ) : (
          <ArtifactNameCell
            artifact={params.row}
            href={getArtifactViewUrl(params.row)}
            permissions={selectionPermissions}
            canManage={canManage}
            isGroupSelected={isGroupSelected}
            onToggleFavorite={() => onToggleFavorite(params.row.id)}
          >
            <GridCellCheckboxRenderer {...params} />
          </ArtifactNameCell>
        ),
    }),
    [apiRef, canManage, isGroupSelected, onToggleFavorite, toggleAllGridRows, toggleSingleGridRow]
  );

  const columns = useMemo(
    () => getColumns({ onChangeGroup, onShare, onDelete, onMenuToggle: setMenuArtifactId }),
    [onChangeGroup, onShare, onDelete]
  );
  const columnVisibilityModel = useMemo(() => ({ actions: canManage, status: canManage }), [canManage]);
  const rowSelectionModel = useMemo(() => getRowSelectionModelFromIds(selectedIds), [selectedIds]);
  const selectedArtifacts = useMemo(
    () => rows.filter((row) => selectedIds.includes(row.id)).map(toBulkActionReport),
    [rows, selectedIds]
  );

  const getRowClassName = useCallback(
    (params: GridRowClassNameParams<ArtifactRow>) => (params.id === menuArtifactId ? "Mui-hovered" : ""),
    [menuArtifactId]
  );

  const handleRowSelectionChange = useCallback(
    (model: GridRowSelectionModel) => setSelectedIds(getSelectedRowIdsFromModel(model)),
    []
  );

  const handleMoveSelected = useCallback(
    (artifactIds: string[]) => onChangeGroup(artifactIds, () => setSelectedIds([])),
    [onChangeGroup]
  );

  const handleDeleteSelected = useCallback(
    (artifactIds: string[]) =>
      onDelete(
        rows.filter((row) => artifactIds.includes(row.id)),
        () => setSelectedIds([])
      ),
    [onDelete, rows]
  );

  return (
    <DataGridHeaderToolbarProvider
      showToolbar={selectedIds.length > 0}
      style={toolbarStyle}
      fullSelection={rows.length > 0 && selectedArtifacts.length === rows.length}
      selection={[selectedIds, setSelectedIds]}
      actions={
        <CustomToolbarActions
          selectedReports={selectedArtifacts}
          onMove={handleMoveSelected}
          onDelete={handleDeleteSelected}
          hideCopy
        />
      }
    >
      <DataGrid<ArtifactRow>
        apiRef={apiRef}
        treeData
        getTreeDataPath={isGroupSelected ? getFlatTreeDataPath : getGroupedTreeDataPath}
        groupingColDef={groupingColDef}
        defaultGroupingExpansionDepth={isGroupSelected ? 0 : 1}
        isGroupExpandedByDefault={isGroupSelected ? undefined : isGroupExpandedByDefault}
        getRowId={getRowId}
        getRowHeight={getRowHeight}
        getRowClassName={getRowClassName}
        isRowSelectable={isRowSelectable}
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={handleRowSelectionChange}
        loading={loading}
        noRowsText={noRowsText}
        columns={columns}
        columnVisibilityModel={columnVisibilityModel}
        rows={rows}
        slots={gridSlots}
        sx={getReportsGridSx}
        sortingOrder={sortingOrder}
        disableMultipleColumnsSorting
        initialState={initialGridState}
        disableColumnReorder
      />
    </DataGridHeaderToolbarProvider>
  );
};

export default ArtifactsGrid;
